import { proxy, proxyUsage } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { createSelectSchema } from 'drizzle-typebox';
import { StringEnum } from '../utils/typebox';

export type ProxyUsage = typeof proxyUsage.$inferSelect;
export type InsertProxyUsage = typeof proxyUsage.$inferInsert;

export const ProxySchema = createSelectSchema(proxy);

export type Proxy = typeof proxy.$inferSelect;
export type InsertProxy = typeof proxy.$inferInsert;

export const CustomProxySchema = Type.Object(
  {
    host: Type.String({ description: 'The host of the proxy.' }),
    port: Type.Number({ description: 'The port of the proxy.' }),
    username: Type.Optional(Type.String({ description: 'The username to connect to the proxy.' })),
    password: Type.Optional(Type.String({ description: 'The password to connect to the proxy.' })),
    protocol: Type.Optional(
      StringEnum(['https', 'http', 'socks5', 'socks4'], {
        default: 'https',
        description: `The protocol of the proxy. Defaults to \`https\`.
        - \`https\` is HTTPS.
        - \`http\` is HTTP.
          - \`socks5\` is SOCKS5.
          - \`socks4\` is SOCKS4.`,
      }),
    ),
  },
  {
    title: 'Custom proxy',
    description:
      'A custom proxy used to connect to the account provider. When Automatic Proxy Protection applies to the provider, Unipile manages the proxy by default. A custom proxy overrides Automatic Proxy Protection. Read more in the Proxy documentation.',
  },
);

export type CustomProxy = Static<typeof CustomProxySchema>;

export const AutoProxyConfigSchema = Type.Object(
  {
    country: Type.Optional(
      Type.String({
        description:
          'An ISO 3166-1 alpha-2 country code for the automatic proxy location, normalized to uppercase. Choose from the <a href="https://developer.unipile.com/v2.0/docs/proxys#countries">countries where Unipile offers automatic proxies</a>. A proxy may be temporarily unavailable even in a country included in the offering.',
      }),
    ),
    ip: Type.Optional(
      Type.String({
        description:
          'An IPv4 address used to infer the automatic proxy location. If Unipile does not offer proxies in the inferred country, or no proxy is currently available there, allow_country_fallback controls whether an available proxy from another country may be used.',
      }),
    ),
    allow_country_fallback: Type.Optional(
      Type.Boolean({
        default: true,
        description:
          'Whether an available proxy from another country may be used when none is available in the rotation country, during authentication and throughout the account lifetime. Defaults to true. An explicitly selected country must be part of Unipile’s proxy offering for the provider, even when fallback is allowed. A proxy may be temporarily unavailable in a country included in the offering. Authentication fails if no proxy is available in any allowed country. If false, authentication fails when no proxy is available in the rotation country. If the selected proxy later fails and no replacement is available there, the account enters Service interruption. Rotation to another available proxy in that country remains allowed.',
      }),
    ),
  },
  {
    description:
      'Country preferences for automatic proxy assignment. Explicitly selected countries must be part of Unipile’s proxy offering for the provider. If omitted, Unipile determines the preferred country automatically. The fallback setting determines whether an available proxy from another country may be used when no proxy is available in the preferred country.',
    title: 'Automatic proxy configuration',
  },
);

export type AutoProxyConfig = Static<typeof AutoProxyConfigSchema>;
